import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { slingshotViewStyles as styles } from './SlingshotView.styles';
import { WarmupToast } from '../WarmupToast/WarmupToast';
import {
  SLINGSHOT_TEST_IDS,
  SLINGSHOT_SESSION_COIN_CAP,
  SLINGSHOT_LEVEL_COUNT,
} from '../../../src/quiz/constants/slingshot.constants';
import type { SlingshotMilestone } from '../../../src/quiz/hooks/useSlingshotGame';
import { WARMUP_COLORS } from '../../../src/quiz/constants/warmup.constants';

/** Rotating hype lines — indexed by which milestone (level/10) so consecutive milestones differ. */
const MINOR_HYPE = ['On fire! 🔥', 'Wall demolisher! 💥', 'Unstoppable! ⚡', 'Ice-cold finisher! 🧊', 'Pure power! 🚀'];
const MAJOR_HYPE = ['Halfway hero! 🌟', 'Elite striker! 🏆', 'Legendary run! 👑'];
function hypeFor(m: SlingshotMilestone): string {
  const i = Math.floor(m.level / 10) - 1;
  return m.tier === 'major' ? MAJOR_HYPE[i % MAJOR_HYPE.length] : MINOR_HYPE[i % MINOR_HYPE.length];
}

export interface SlingshotOverlaysProps {
  status: 'loading' | 'ready' | 'error';
  errorMessage: string | null;
  coinsLeftToday: number;
  capReached: boolean;
  bestLevel: number;
  coinsBeat: number;
  newBest: boolean;
  completed: boolean;
  finalLevel: number;
  toast: string | null;
  showExitSummary: boolean;
  levelReached: number;
  levelsCleared: number;
  sessionCoins: number;
  milestone: SlingshotMilestone | null;
  onRetry: () => void;
  onPlayAgain: () => void;
  onMoreGames: () => void;
  onLeave: () => void;
  onKeepPlaying: () => void;
  onMilestoneContinue: () => void;
}

/** One label/value line in the exit-summary card. */
function SummaryRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <View style={styles.summaryRow}>
      <Text style={styles.summaryLabel}>{label}</Text>
      <Text style={[styles.summaryValue, highlight && styles.summaryValueHot]}>{value}</Text>
    </View>
  );
}

/**
 * Presentational overlay layer for the slingshot game (loading/error/completion, the "+N" beat, toast, and
 * the exit summary). Prop-driven and WebView-free so it can be storied deterministically across states.
 */
export function SlingshotOverlays(props: SlingshotOverlaysProps) {
  const coinsUsed = Math.min(SLINGSHOT_SESSION_COIN_CAP - props.coinsLeftToday, SLINGSHOT_SESSION_COIN_CAP);

  return (
    <>
      <View style={styles.coinRow}>
        <View style={styles.coinDot} />
        <Text style={styles.coinProgress} testID={SLINGSHOT_TEST_IDS.coinProgress}>
          {`${coinsUsed} / ${SLINGSHOT_SESSION_COIN_CAP} coins today`}
        </Text>
        {props.bestLevel > 0 ? <Text style={styles.bestText}>{`· Best: Level ${props.bestLevel}`}</Text> : null}
        {props.capReached ? (
          <Text style={styles.capBanner} testID={SLINGSHOT_TEST_IDS.capBanner}>· Record mode 🔥</Text>
        ) : null}
      </View>

      {props.status === 'loading' ? (
        <View style={styles.overlay} testID={SLINGSHOT_TEST_IDS.loading} pointerEvents="none">
          <ActivityIndicator color={WARMUP_COLORS.purple} />
        </View>
      ) : null}

      {props.status === 'error' ? (
        <View style={styles.overlay} testID={SLINGSHOT_TEST_IDS.error}>
          <Text style={styles.message}>{props.errorMessage ?? "Couldn't load the game."}</Text>
          <Pressable
            testID={SLINGSHOT_TEST_IDS.retry}
            onPress={props.onRetry}
            style={({ pressed }) => [styles.retryButton, pressed && styles.pressedDim]}
          >
            <Text style={styles.retryText}>Retry</Text>
          </Pressable>
        </View>
      ) : null}

      {props.completed ? (
        <View style={styles.overlay} testID={SLINGSHOT_TEST_IDS.endOverlay}>
          <Text style={styles.endTitle}>Campaign complete! 🏆</Text>
          <Text style={styles.endSub}>{`You reached Level ${props.finalLevel}`}</Text>
          {props.newBest ? <Text style={styles.endBest}>New best! 🔥</Text> : null}
          <View style={styles.endButtons}>
            <Pressable
              testID={SLINGSHOT_TEST_IDS.playAgain}
              onPress={props.onPlayAgain}
              style={({ pressed }) => [styles.retryButton, pressed && styles.pressedDim]}
            >
              <Text style={styles.retryText}>Play again</Text>
            </Pressable>
            <Pressable
              testID={SLINGSHOT_TEST_IDS.moreGames}
              onPress={props.onMoreGames}
              style={({ pressed }) => [styles.ghostButton, pressed && styles.pressedDim]}
            >
              <Text style={styles.ghostText}>More games</Text>
            </Pressable>
          </View>
        </View>
      ) : null}

      {props.showExitSummary && !props.completed ? (
        <View style={styles.overlay} testID={SLINGSHOT_TEST_IDS.exitSummary}>
          <View style={styles.summaryCard}>
            <Text style={styles.endTitle}>Nice run! ⚽</Text>
            <View style={styles.summaryRows}>
              <SummaryRow label="Level reached" value={`${props.levelReached || 1}`} />
              <SummaryRow label="Levels cleared" value={`${props.levelsCleared}`} />
              <SummaryRow label="Coins this session" value={`+${props.sessionCoins} 🪙`} highlight />
              <SummaryRow
                label="Best level"
                value={props.newBest ? `${props.bestLevel}  NEW 🔥` : `${props.bestLevel}`}
                highlight={props.newBest}
              />
              <SummaryRow
                label="Today"
                value={props.capReached ? 'Daily cap reached 🎉' : `${props.coinsLeftToday} coins left`}
              />
            </View>
            <View style={styles.endButtons}>
              <Pressable
                testID={SLINGSHOT_TEST_IDS.exitKeepPlaying}
                onPress={props.onKeepPlaying}
                style={({ pressed }) => [styles.retryButton, pressed && styles.pressedDim]}
              >
                <Text style={styles.retryText}>Keep playing</Text>
              </Pressable>
              <Pressable
                testID={SLINGSHOT_TEST_IDS.exitLeave}
                onPress={props.onLeave}
                style={({ pressed }) => [styles.ghostButton, pressed && styles.pressedDim]}
              >
                <Text style={styles.ghostText}>Leave</Text>
              </Pressable>
            </View>
          </View>
        </View>
      ) : null}

      {props.milestone && !props.completed ? (
        <View style={styles.overlay} testID={SLINGSHOT_TEST_IDS.milestone}>
          <View style={[styles.summaryCard, props.milestone.tier === 'major' && styles.milestoneMajor]}>
            <Text style={styles.milestoneBadge}>{props.milestone.tier === 'major' ? '🏆' : '🎉'}</Text>
            <Text style={styles.endTitle}>{`Level ${props.milestone.level} milestone!`}</Text>
            <Text style={styles.milestoneHype}>{hypeFor(props.milestone)}</Text>
            <View style={styles.summaryRows}>
              <SummaryRow label="Coins this session" value={`+${props.sessionCoins} 🪙`} highlight />
              <SummaryRow
                label="Best level"
                value={props.newBest ? `${props.bestLevel}  NEW 🔥` : `${props.bestLevel}`}
                highlight={props.newBest}
              />
              {props.milestone.level + 10 <= SLINGSHOT_LEVEL_COUNT ? (
                <SummaryRow label="Next milestone" value={`Level ${props.milestone.level + 10}`} />
              ) : null}
            </View>
            <Pressable
              testID={SLINGSHOT_TEST_IDS.milestoneContinue}
              onPress={props.onMilestoneContinue}
              style={({ pressed }) => [styles.retryButton, pressed && styles.pressedDim]}
            >
              <Text style={styles.retryText}>Keep going ⚽</Text>
            </Pressable>
          </View>
        </View>
      ) : null}

      {props.coinsBeat > 0 ? (
        <View pointerEvents="none" style={styles.coinBeat}>
          <Text style={styles.coinBeatText}>{`+${props.coinsBeat}`}</Text>
        </View>
      ) : null}

      <WarmupToast message={props.toast} />
    </>
  );
}
